/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarNavSection';

const SidebarNavSectionManager: ComponentManagerFn<'SidebarNavSection'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarNavSection'>(platform, 'SidebarNavSection'),
    type: 'SidebarNavSection',
    name: 'Sidebar Nav Section',
    isChildAllowed: (page, item, childType) =>
      (['SidebarNavLink'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['SidebarNavLink', 'SidebarNavLink', 'SidebarNavLink'],
    props: {
      navSectionHeight: {
        label: 'Height',
        type: 'number',
        min: 100,
        max: 800,
        step: 40,
      },
    },
    getDisplayText: (item) => '',
    category: 'Sidebar',
    canHaveChildren: () => true,
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Sidebar Nav Section is a container component designed to group related navigation links (SidebarNavLink) together within a Sidebar Panel. It acts as a structural block, ensuring that a set of dynamic links are presented as a cohesive unit.
Use this component to organize your main application menu into logical groups, such as "Favorites," "Modules," or "Administration."

**1. Structure & Children**<br/>
This component functions as a specific wrapper for navigation links.
* **Allowed Children:** It strictly accepts SidebarNavLink components. This ensures that only dynamic, expression-based links are added to this section.
* **Default Content:** When added, it automatically initializes with three SidebarNavLink children, providing a ready-to-use menu block.

**2. Layout Configuration**<br/>
* **Height:** You can enforce a fixed height for the section (navSectionHeight) ranging from 100px to 800px. This is useful for creating scrollable areas within the sidebar or reserving specific vertical space for a menu group.

**3. Best Use Cases**<br/>
* **Module Grouping:** Creating distinct sections for "Sales" (Leads, Opportunities) and "Service" (Tickets, FAQs).
* **Scrollable Menus:** Defining a fixed-height section for a long list of generated links, preventing the entire sidebar from growing too large.
* **Visual Separation:** Using sections to create white space or distinct blocks between primary navigation and secondary utility links.`,
  };
};

export default SidebarNavSectionManager;
